import type { Category, CategoryInput, CategoryUpdateInput } from '../types/category';
import type {
  AdminProductsQuery,
  ProductCard,
  ProductCreateInput,
  ProductDetail,
  ProductUpdateInput,
  PublicProductsQuery,
} from '../types/product';
import type { Slider, SliderCreateInput, SliderUpdateInput } from '../types/slider';
import type { SiteSettings, SiteSettingsFiles, SiteSettingsTextKey } from '../types/siteSettings';

const API_BASE_URL = 'https://cms.orionclickz.com/a-new/public/api/';

const TOKEN_STORAGE_KEY = 'brix-admin-token';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface Admin {
  id: number;
  name: string;
  email: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data?: T;
}

export interface AdminLoginData {
  token: string;
  token_type: string;
  admin: Admin;
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown | FormData;
  auth?: boolean;
  query?: Record<string, string | number | boolean | undefined | null>;
};

function joinUrl(path: string): string {
  const base = API_BASE_URL.endsWith('/') ? API_BASE_URL : `${API_BASE_URL}/`;
  const segment = path.startsWith('/') ? path.slice(1) : path;
  return `${base}${segment}`;
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

function messageFromBody(body: unknown, fallback: string): string {
  if (!body || typeof body !== 'object') return fallback;
  const record = body as Record<string, unknown>;
  if (typeof record.message === 'string' && record.message) return record.message;
  const errors = record.errors;
  if (errors && typeof errors === 'object') {
    const first = Object.values(errors as Record<string, unknown>)[0];
    if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
  }
  return fallback;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, auth = false, headers: initHeaders, query, ...rest } = options;

  const headers = new Headers(initHeaders);
  headers.set('Accept', 'application/json');
  const isFormData = body instanceof FormData;
  if (body !== undefined && !isFormData) {
    headers.set('Content-Type', 'application/json');
  }

  if (auth) {
    const token = getStoredToken();
    if (!token) {
      throw new ApiError('Unauthenticated', 401);
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  let url = joinUrl(path);
  if (query) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }
    const qs = params.toString();
    if (qs) url = `${url}?${qs}`;
  }

  const response = await fetch(url, {
    ...rest,
    headers,
    body:
      body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
  });

  let parsed: unknown;
  const text = await response.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    const fallback =
      response.status === 401
        ? 'Invalid email or password'
        : response.status === 429
          ? 'Too many login attempts. Please try again in a minute.'
          : `Request failed (${response.status})`;
    throw new ApiError(messageFromBody(parsed, fallback), response.status, parsed);
  }

  return parsed as T;
}

/** POST /admin/login — authenticate and store the Sanctum bearer token. */
export async function adminLogin(email: string, password: string): Promise<AdminLoginData> {
  const envelope = await request<ApiEnvelope<AdminLoginData>>('admin/login', {
    method: 'POST',
    body: { email, password },
  });

  if (!envelope.data?.token) {
    throw new ApiError(envelope.message || 'Login failed', 500, envelope);
  }

  setStoredToken(envelope.data.token);
  return envelope.data;
}

/** POST /admin/logout — revoke the current bearer token. */
export async function adminLogout(): Promise<void> {
  try {
    await request<ApiEnvelope<unknown>>('admin/logout', {
      method: 'POST',
      auth: true,
    });
  } finally {
    clearStoredToken();
  }
}

/** GET /admin/me — return the admin for the current bearer token. */
export async function adminMe(): Promise<Admin> {
  const envelope = await request<ApiEnvelope<Admin>>('admin/me', {
    method: 'GET',
    auth: true,
  });

  if (!envelope.data) {
    throw new ApiError(envelope.message || 'Failed to load admin profile', 500, envelope);
  }

  return envelope.data;
}

function unwrapSettings(envelope: ApiEnvelope<SiteSettings>): SiteSettings {
  if (!envelope.data) {
    throw new ApiError(envelope.message || 'Failed to load settings', 500, envelope);
  }
  return envelope.data;
}

/** GET /settings — public site settings. */
export async function getPublicSettings(): Promise<SiteSettings> {
  const envelope = await request<ApiEnvelope<SiteSettings>>('settings', { method: 'GET' });
  return unwrapSettings(envelope);
}

/** GET /admin/settings — authenticated site settings. */
export async function getAdminSettings(): Promise<SiteSettings> {
  const envelope = await request<ApiEnvelope<SiteSettings>>('admin/settings', {
    method: 'GET',
    auth: true,
  });
  return unwrapSettings(envelope);
}

/** PUT /admin/settings — update site settings (JSON or multipart with files). */
export async function updateAdminSettings(
  fields: Partial<Record<SiteSettingsTextKey, string>>,
  files?: SiteSettingsFiles,
): Promise<SiteSettings> {
  const hasFiles = files && Object.values(files).some(Boolean);

  if (hasFiles) {
    const form = new FormData();
    form.append('_method', 'PUT');
    for (const [key, value] of Object.entries(fields)) {
      if (value !== undefined) form.append(key, value);
    }
    for (const [key, file] of Object.entries(files ?? {})) {
      if (file) form.append(key, file);
    }
    const envelope = await request<ApiEnvelope<SiteSettings>>('admin/settings', {
      method: 'POST',
      body: form,
      auth: true,
    });
    return unwrapSettings(envelope);
  }

  const envelope = await request<ApiEnvelope<SiteSettings>>('admin/settings', {
    method: 'PUT',
    body: fields,
    auth: true,
  });
  return unwrapSettings(envelope);
}

function unwrapCategory(envelope: ApiEnvelope<Category>): Category {
  if (!envelope.data) {
    throw new ApiError(envelope.message || 'Failed to load category', 500, envelope);
  }
  return envelope.data;
}

function unwrapCategoryList(envelope: ApiEnvelope<Category[]>): Category[] {
  if (!envelope.data) {
    throw new ApiError(envelope.message || 'Failed to load categories', 500, envelope);
  }
  return envelope.data;
}

function appendCategoryFields(form: FormData, input: CategoryUpdateInput): void {
  if (input.name !== undefined) form.append('name', input.name);
  if (input.is_active !== undefined) {
    form.append('is_active', input.is_active ? '1' : '0');
  }
  if (input.image) form.append('image', input.image);
}

/** GET /categories — active categories (public). */
export async function getPublicCategories(): Promise<Category[]> {
  const envelope = await request<ApiEnvelope<Category[]>>('categories', { method: 'GET' });
  return unwrapCategoryList(envelope);
}

/** GET /categories/{category} — single active category (public). */
export async function getPublicCategory(id: number): Promise<Category> {
  const envelope = await request<ApiEnvelope<Category>>(`categories/${id}`, { method: 'GET' });
  return unwrapCategory(envelope);
}

/** GET /admin/categories — all categories (admin). */
export async function getAdminCategories(): Promise<Category[]> {
  const envelope = await request<ApiEnvelope<Category[]>>('admin/categories', {
    method: 'GET',
    auth: true,
  });
  return unwrapCategoryList(envelope);
}

/** GET /admin/categories/{category} — single category (admin). */
export async function getAdminCategory(id: number): Promise<Category> {
  const envelope = await request<ApiEnvelope<Category>>(`admin/categories/${id}`, {
    method: 'GET',
    auth: true,
  });
  return unwrapCategory(envelope);
}

/** POST /admin/categories — create a category. */
export async function createCategory(input: CategoryInput): Promise<Category> {
  const hasImage = Boolean(input.image);

  if (hasImage) {
    const form = new FormData();
    appendCategoryFields(form, input);
    const envelope = await request<ApiEnvelope<Category>>('admin/categories', {
      method: 'POST',
      body: form,
      auth: true,
    });
    return unwrapCategory(envelope);
  }

  const envelope = await request<ApiEnvelope<Category>>('admin/categories', {
    method: 'POST',
    body: {
      name: input.name,
      ...(input.is_active !== undefined ? { is_active: input.is_active } : {}),
    },
    auth: true,
  });
  return unwrapCategory(envelope);
}

/** PUT /admin/categories/{category} — update a category. */
export async function updateCategory(id: number, input: CategoryUpdateInput): Promise<Category> {
  const hasImage = Boolean(input.image);

  if (hasImage) {
    const form = new FormData();
    form.append('_method', 'PUT');
    appendCategoryFields(form, input);
    const envelope = await request<ApiEnvelope<Category>>(`admin/categories/${id}`, {
      method: 'POST',
      body: form,
      auth: true,
    });
    return unwrapCategory(envelope);
  }

  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name;
  if (input.is_active !== undefined) body.is_active = input.is_active;

  const envelope = await request<ApiEnvelope<Category>>(`admin/categories/${id}`, {
    method: 'PUT',
    body,
    auth: true,
  });
  return unwrapCategory(envelope);
}

/** DELETE /admin/categories/{category} — delete a category (cascades to products). */
export async function deleteCategory(id: number): Promise<void> {
  await request<ApiEnvelope<unknown>>(`admin/categories/${id}`, {
    method: 'DELETE',
    auth: true,
  });
}

interface PaginatedEnvelope<T> extends ApiEnvelope<T[]> {
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

function unwrapProduct(envelope: ApiEnvelope<ProductDetail>): ProductDetail {
  if (!envelope.data) {
    throw new ApiError(envelope.message || 'Failed to load product', 500, envelope);
  }
  return envelope.data;
}

function unwrapProductList(envelope: PaginatedEnvelope<ProductCard>): ProductCard[] {
  if (!envelope.data) {
    throw new ApiError(envelope.message || 'Failed to load products', 500, envelope);
  }
  return envelope.data;
}

function appendProductFields(
  form: FormData,
  input: ProductCreateInput | ProductUpdateInput,
): void {
  if (input.category_id !== undefined) {
    form.append('category_id', String(input.category_id));
  }
  if (input.name !== undefined) form.append('name', input.name);
  if (input.description !== undefined) form.append('description', input.description);
  if (input.price !== undefined) form.append('price', String(input.price));
  if (input.sale_price !== undefined && input.sale_price !== null) {
    form.append('sale_price', String(input.sale_price));
  }
  if (input.stock !== undefined) form.append('stock', String(input.stock));
  if (input.is_active !== undefined) {
    form.append('is_active', input.is_active ? '1' : '0');
  }
  if (input.image) form.append('image', input.image);
  if (input.gallery?.length) {
    for (const file of input.gallery) {
      form.append('gallery[]', file);
    }
  }
}

function productUpdateUsesMultipart(input: ProductUpdateInput): boolean {
  return Boolean(input.image || (input.gallery && input.gallery.length > 0));
}

/** GET /products — paginated public product list. */
export async function getPublicProducts(
  query: PublicProductsQuery = {},
): Promise<PaginatedEnvelope<ProductCard>> {
  return request<PaginatedEnvelope<ProductCard>>('products', {
    method: 'GET',
    query: query as Record<string, string | number | boolean | undefined | null>,
  });
}

/** GET /products/{product} — public product detail. */
export async function getPublicProduct(id: number): Promise<ProductDetail> {
  const envelope = await request<ApiEnvelope<ProductDetail>>(`products/${id}`, { method: 'GET' });
  return unwrapProduct(envelope);
}

/** GET /admin/products — paginated admin product list. */
export async function getAdminProducts(
  query: AdminProductsQuery = {},
): Promise<PaginatedEnvelope<ProductCard>> {
  return request<PaginatedEnvelope<ProductCard>>('admin/products', {
    method: 'GET',
    auth: true,
    query: query as Record<string, string | number | boolean | undefined | null>,
  });
}

/** Fetch all admin products (pages through results, up to 100 per page). */
export async function getAllAdminProducts(): Promise<ProductCard[]> {
  const per_page = 100;
  let page = 1;
  const items: ProductCard[] = [];

  for (;;) {
    const envelope = await getAdminProducts({ page, per_page });
    items.push(...unwrapProductList(envelope));
    if (page >= envelope.meta.last_page) break;
    page += 1;
  }

  return items;
}

/** GET /admin/products/{product} — admin product detail. */
export async function getAdminProduct(id: number): Promise<ProductDetail> {
  const envelope = await request<ApiEnvelope<ProductDetail>>(`admin/products/${id}`, {
    method: 'GET',
    auth: true,
  });
  return unwrapProduct(envelope);
}

/** POST /admin/products — create a product (multipart). */
export async function createProduct(input: ProductCreateInput): Promise<ProductDetail> {
  const form = new FormData();
  appendProductFields(form, input);
  const envelope = await request<ApiEnvelope<ProductDetail>>('admin/products', {
    method: 'POST',
    body: form,
    auth: true,
  });
  return unwrapProduct(envelope);
}

/** PUT /admin/products/{product} — update a product. */
export async function updateProduct(id: number, input: ProductUpdateInput): Promise<ProductDetail> {
  if (productUpdateUsesMultipart(input)) {
    const form = new FormData();
    form.append('_method', 'PUT');
    appendProductFields(form, input);
    const envelope = await request<ApiEnvelope<ProductDetail>>(`admin/products/${id}`, {
      method: 'POST',
      body: form,
      auth: true,
    });
    return unwrapProduct(envelope);
  }

  const body: Record<string, unknown> = {};
  if (input.category_id !== undefined) body.category_id = input.category_id;
  if (input.name !== undefined) body.name = input.name;
  if (input.description !== undefined) body.description = input.description;
  if (input.price !== undefined) body.price = input.price;
  if (input.sale_price !== undefined) body.sale_price = input.sale_price;
  if (input.stock !== undefined) body.stock = input.stock;
  if (input.is_active !== undefined) body.is_active = input.is_active;

  const envelope = await request<ApiEnvelope<ProductDetail>>(`admin/products/${id}`, {
    method: 'PUT',
    body,
    auth: true,
  });
  return unwrapProduct(envelope);
}

/** DELETE /admin/products/{product} — delete a product. */
export async function deleteProduct(id: number): Promise<void> {
  await request<ApiEnvelope<unknown>>(`admin/products/${id}`, {
    method: 'DELETE',
    auth: true,
  });
}

function unwrapSlider(envelope: ApiEnvelope<Slider>): Slider {
  if (!envelope.data) {
    throw new ApiError(envelope.message || 'Failed to load slider', 500, envelope);
  }
  return envelope.data;
}

function unwrapSliderList(envelope: ApiEnvelope<Slider[]>): Slider[] {
  if (!envelope.data) {
    throw new ApiError(envelope.message || 'Failed to load sliders', 500, envelope);
  }
  return envelope.data;
}

function appendSliderFields(form: FormData, input: SliderCreateInput | SliderUpdateInput): void {
  if (input.title !== undefined) form.append('title', input.title);
  if (input.subtitle !== undefined) form.append('subtitle', input.subtitle);
  if (input.link !== undefined) form.append('link', input.link);
  if (input.sort_order !== undefined) form.append('sort_order', String(input.sort_order));
  if (input.is_active !== undefined) {
    form.append('is_active', input.is_active ? '1' : '0');
  }
  if (input.image) form.append('image', input.image);
}

function sliderUpdateUsesMultipart(input: SliderUpdateInput): boolean {
  return Boolean(input.image);
}

/** GET /sliders — active sliders (public). */
export async function getPublicSliders(): Promise<Slider[]> {
  const envelope = await request<ApiEnvelope<Slider[]>>('sliders', { method: 'GET' });
  return unwrapSliderList(envelope);
}

/** GET /admin/sliders — all sliders (admin). */
export async function getAdminSliders(): Promise<Slider[]> {
  const envelope = await request<ApiEnvelope<Slider[]>>('admin/sliders', {
    method: 'GET',
    auth: true,
  });
  return unwrapSliderList(envelope);
}

/** GET /admin/sliders/{slider} — single slider (admin). */
export async function getAdminSlider(id: number): Promise<Slider> {
  const envelope = await request<ApiEnvelope<Slider>>(`admin/sliders/${id}`, {
    method: 'GET',
    auth: true,
  });
  return unwrapSlider(envelope);
}

/** POST /admin/sliders — create a slider (multipart). */
export async function createSlider(input: SliderCreateInput): Promise<Slider> {
  const form = new FormData();
  appendSliderFields(form, input);
  const envelope = await request<ApiEnvelope<Slider>>('admin/sliders', {
    method: 'POST',
    body: form,
    auth: true,
  });
  return unwrapSlider(envelope);
}

/** PUT /admin/sliders/{slider} — update a slider. */
export async function updateSlider(id: number, input: SliderUpdateInput): Promise<Slider> {
  if (sliderUpdateUsesMultipart(input)) {
    const form = new FormData();
    form.append('_method', 'PUT');
    appendSliderFields(form, input);
    const envelope = await request<ApiEnvelope<Slider>>(`admin/sliders/${id}`, {
      method: 'POST',
      body: form,
      auth: true,
    });
    return unwrapSlider(envelope);
  }

  const body: Record<string, unknown> = {};
  if (input.title !== undefined) body.title = input.title;
  if (input.subtitle !== undefined) body.subtitle = input.subtitle;
  if (input.link !== undefined) body.link = input.link;
  if (input.sort_order !== undefined) body.sort_order = input.sort_order;
  if (input.is_active !== undefined) body.is_active = input.is_active;

  const envelope = await request<ApiEnvelope<Slider>>(`admin/sliders/${id}`, {
    method: 'PUT',
    body,
    auth: true,
  });
  return unwrapSlider(envelope);
}

/** DELETE /admin/sliders/{slider} — delete a slider. */
export async function deleteSlider(id: number): Promise<void> {
  await request<ApiEnvelope<unknown>>(`admin/sliders/${id}`, {
    method: 'DELETE',
    auth: true,
  });
}

export { API_BASE_URL, TOKEN_STORAGE_KEY };
